import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Body1,
    Button,
    Heading2,
    Image,
    Stack,
} from '@myphx-engineering/phx-design-system';

import { useMedicationConfigOverride } from '../../../../contexts/DrugProgramProvider';
import {
    type DigitallyReceivedPrescriberOrderInfoFragment,
    type MailOrderProviderInfoFragment,
    type PharmacyProviderInfoFragment,
    type PrescriberInfoFragment,
    PrescriberOrderStatus,
    type UnknownProviderInfoFragment,
} from '../../../../graphql/generated/graphql';
import type { PharmacyOverride } from '../../../../hooks/use-config-api';
import { useFlags } from '../../../../hooks/use-flags';

import { ProviderNextSteps } from './ProviderNextSteps';

export type ProviderOverridesProps = {
    prescriber: PrescriberInfoFragment;
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
    provider:
        | MailOrderProviderInfoFragment
        | PharmacyProviderInfoFragment
        | UnknownProviderInfoFragment;
};

export const ProviderOverrides: FC<ProviderOverridesProps> = ({
    prescriber,
    prescriberOrder,
    provider,
}) => {
    const { t } = useTranslation('lilly');
    const { enableRxCancelLilly } = useFlags();

    const configOverride = useMedicationConfigOverride();

    const providerOverrides: PharmacyOverride | undefined =
        configOverride.pharmacyOverrides[provider.id];
    const providerName =
        providerOverrides?.name ??
        (provider.__typename === 'MailOrderProvider' ||
        provider.__typename === 'PharmacyProvider'
            ? provider.name
            : 'Unknown Provider');

    const isCancelled =
        prescriberOrder.status === PrescriberOrderStatus.Cancelled;
    const prescriberPhone = prescriber?.phoneNumber;

    if (!providerOverrides) {
        return null;
    }

    return (
        <Stack gap="sm" data-testid="order-confirmation-next-steps">
            <Stack align="center">
                <Image
                    src={providerOverrides.logoUrl}
                    alt={`${providerName} logo`}
                    w={providerOverrides.logoWidth}
                    h="50"
                />
            </Stack>
            <Heading2>{t('orderConfirmation.nextSteps.header')}</Heading2>
            {isCancelled && enableRxCancelLilly ? (
                <Stack gap="sm">
                    <Body1>{t('prescription.cancelled.modal.body1')}</Body1>
                    <Body1 bold>
                        {t('prescription.cancelled.modal.body2')}
                    </Body1>
                    {prescriberPhone && (
                        <Button
                            onClick={() => {
                                window.location.href = `tel:${prescriberPhone}`;
                            }}
                        >
                            {t('prescription.cancelled.modal.callProvider')}
                        </Button>
                    )}
                </Stack>
            ) : (
                <ProviderNextSteps
                    prescriber={prescriber}
                    prescriberOrder={prescriberOrder}
                    provider={provider}
                />
            )}
        </Stack>
    );
};
